import type { EmblaViewportRefType } from 'embla-carousel-react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from '@/components/pages/icons';
import { Block } from '../block';
import { QuoteCardTitle } from './parts';
import { QuoteCarouselItem } from './item';
import type { QuoteCarouselProps } from './type';

type QuoteCarouselServerProps = QuoteCarouselProps & {
  emblaRef?: EmblaViewportRefType;
  canScrollPrev?: boolean;
  canScrollNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
};

export function QuoteCarouselServer({
  title,
  quotes,
  emblaRef,
  canScrollPrev,
  canScrollNext,
  onPrev,
  onNext,
}: QuoteCarouselServerProps) {
  return (
    <section className="w-full bg-[#e0f2fe]">
      <Block>
        <div className="mx-auto w-full max-w-page px-4 @lg:px-8">
          <QuoteCardTitle title={title} />
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {quotes.map((item, index) => (
              <div key={index} className="min-w-0 shrink-0 grow-0 basis-full px-5 @lg:px-8">
                <div className="mx-auto w-full max-w-page">
                  <QuoteCarouselItem {...item} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {quotes.length > 1 && (
          <div className="px-4 @lg:px-8">
            <div className="mx-auto w-full max-w-page @lg:-translate-y-full">
              <div className="flex @lg:gap-16 mt-4 @lg:mt-0">
                <div className="hidden @lg:block @lg:min-w-[559px] @lg:shrink-0" />
                <div className="flex gap-4 flex-1 justify-end @lg:justify-start">
                  <CarouselNavButton direction="prev" disabled={!canScrollPrev} onClick={onPrev} />
                  <CarouselNavButton direction="next" disabled={!canScrollNext} onClick={onNext} />
                </div>
              </div>
            </div>
          </div>
        )}
      </Block>
    </section>
  );
}

function CarouselNavButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled?: boolean;
  onClick?: () => void;
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Previous quote' : 'Next quote'}
      className={clsx(
        'w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 bg-brand-muted-light',
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:brightness-90 cursor-pointer'
      )}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
}

export default QuoteCarouselServer;
